import PropTypes from 'prop-types';
import { Box, makeStyles } from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
    },

    row: {
        ...theme.mixins.flex.flexDirectionRow,

        '&$hLeft': { ...theme.mixins.flex.justifyContentFlexStart },
        '&$hCenter': { ...theme.mixins.flex.justifyContentCenter },
        '&$hRight': { ...theme.mixins.flex.justifyContentFlexEnd },
        '&$vTop': { ...theme.mixins.flex.alignItemsFlexStart },
        '&$vCenter': { ...theme.mixins.flex.alignItemsCenter },
        '&$vBottom': { ...theme.mixins.flex.alignItemsFlexEnd },
        '&$vBaseline': { ...theme.mixins.flex.alignItemsBaseline },
        '&$vStretch': { ...theme.mixins.flex.alignItemsStretch },
    },

    column: {
        ...theme.mixins.flex.flexDirectionColumn,

        '&$hLeft': { ...theme.mixins.flex.alignItemsFlexStart },
        '&$hCenter': { ...theme.mixins.flex.alignItemsCenter },
        '&$hRight': { ...theme.mixins.flex.alignItemsFlexEnd },
        '&$hBaseline': { ...theme.mixins.flex.alignItemsBaseline },
        '&$hStretch': { ...theme.mixins.flex.alignItemsStretch },
        '&$vTop': { ...theme.mixins.flex.justifyContentFlexStart },
        '&$vCenter': { ...theme.mixins.flex.justifyContentCenter },
        '&$vBottom': { ...theme.mixins.flex.justifyContentFlexEnd },
    },

    grow: {
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),
    },

    // meta-classes, do not put styles directly in them as they are direction dependent
    hLeft: {},
    hCenter: {},
    hRight: {},
    hBaseline: {}, // column only
    hStretch: {}, // column only

    vTop: {},
    vCenter: {},
    vBottom: {},
    vBaseline: {}, // row only
    vStretch: {}, // row only
}));

function FlexLayout(props) {
    const {
        children,
        component: ComponentType,
        className,
        row,
        column,
        grow,
        hLeft,
        hCenter,
        hRight,
        hBaseline,
        hStretch,
        vTop,
        vCenter,
        vBottom,
        vBaseline,
        vStretch,
        ...restProps
    } = props;

    const classes = useStyles();
    const flexClasses = [];

    // Begin validations, there are a number of combinations that don't make sense. Make sure
    // we don't let soemone assume they are using a valid config when they aren't
    if ((row && column) || (!row && !column)) throw new Error('row xor column is required');
    if (row && hStretch) throw new Error('hStretch is column-only'); // use grow instead...
    if (row && hBaseline) throw new Error('hBaseline is column-only');
    if (column && vStretch) throw new Error('vStretch is row-only'); // use grow instead...
    if (column && vBaseline) throw new Error('vBaseline is row-only');

    if ([hLeft, hCenter, hRight, hBaseline, hStretch].filter((v) => !!v).length > 1) {
        throw new Error('Only one horizontal-alignment parameter is suppoted');
    }

    if ([vTop, vCenter, vBottom, vBaseline, vStretch].filter((v) => !!v).length > 1) {
        throw new Error('Only one vertical-alignment parameter is suppoted');
    }

    // End validations

    flexClasses.push(row === true ? classes.row : classes.column);

    if (grow) flexClasses.push(classes.grow);
    if (hLeft) flexClasses.push(classes.hLeft);
    if (hCenter) flexClasses.push(classes.hCenter);
    if (hRight) flexClasses.push(classes.hRight);
    if (hBaseline) flexClasses.push(classes.hBaseline);
    if (hStretch) flexClasses.push(classes.hStretch);
    if (vTop) flexClasses.push(classes.vTop);
    if (vCenter) flexClasses.push(classes.vCenter);
    if (vBottom) flexClasses.push(classes.vBottom);
    if (vBaseline) flexClasses.push(classes.vBaseline);
    if (vStretch) flexClasses.push(classes.vStretch);

    return (
        <ComponentType className={clsx(classes.root, className, flexClasses)} {...restProps}>
            {children}
        </ComponentType>
    );
}

FlexLayout.propTypes = {
    children: PropTypes.node,
    component: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    className: PropTypes.any,
    grow: PropTypes.bool,
    hLeft: PropTypes.bool,
    hCenter: PropTypes.bool,
    hRight: PropTypes.bool,
    hBaseline: PropTypes.bool,
    hStretch: PropTypes.bool,
    vTop: PropTypes.bool,
    vCenter: PropTypes.bool,
    vBottom: PropTypes.bool,
    vBaseline: PropTypes.bool,
    vStretch: PropTypes.bool,
};

FlexLayout.defaultProps = {
    children: null,
    component: Box,
    className: null,
    grow: false,
    hLeft: false,
    hCenter: false,
    hRight: false,
    hBaseline: false,
    hStretch: false,
    vTop: false,
    vCenter: false,
    vBottom: false,
    vBaseline: false,
    vStretch: false,
};

export function RowFlexLayout(props) {
    return <FlexLayout row {...props} />;
}

RowFlexLayout.propTypes = { ...FlexLayout.propTypes };
RowFlexLayout.defaultProps = { ...FlexLayout.defaultProps };

export function ColumnFlexLayout(props) {
    return <FlexLayout column {...props} />;
}

ColumnFlexLayout.propTypes = { ...FlexLayout.propTypes };
ColumnFlexLayout.defaultProps = { ...FlexLayout.defaultProps };
